import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import FileUpload from 'components/doc-components/FileUpload';
import IconButton from 'components/common/IconButton';
import PageHeader from 'components/common/PageHeader';
import FalconComponentCard from 'components/common/FalconComponentCard';
import audiences from '../../config/audiences';

const CreateHomeBannerForm = () => {
  const navigate = useNavigate();
  const [fileUrl, setFileUrl] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_file: null,
    image_filename: null,
    post_date: moment.utc(new Date(), 'YYYY-MM-DD HH:mm').toDate(),
    date_updated: moment.utc(new Date(), 'YYYY-MM-DD HH:mm').toDate(),
    date_expiry: moment.utc(new Date(), 'YYYY-MM-DD HH:mm').toDate(),
    audience: 'public',
    user_id: '',
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setFormData({
          ...formData,
          user_id: uid,
        });
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form data', formData);
    if (
      formData.date_expiry &&
      moment(formData.date_expiry).isBefore(formData.date_added)
    ) {
      toast.error(`Expiry date must be after date added`, {
        theme: 'colored',
      });
      return;
    }
    if (!fileUrl) {
      toast.error(`Banner image file is required`, {
        theme: 'colored',
      });
      return;
    }

    try {
      let formData2 = new FormData();
      formData2.append('title', formData.title);
      formData2.append('date_added', moment.utc().format('YYYY-MM-DD HH:mm'));
      formData2.append('date_updated', moment.utc().format('YYYY-MM-DD HH:mm'));
      formData2.append(
        'post_date',
        formData.post_date &&
          moment.utc(formData.post_date).format('YYYY-MM-DD HH:mm')
      );
      formData2.append(
        'date_expiry',
        formData.date_expiry &&
          moment.utc(formData.date_expiry).format('YYYY-MM-DD HH:mm')
      );
      formData2.append(
        'year_added',
        moment.utc(formData.date_added).format('yyyy')
      );
      formData2.append('fileUrl', fileUrl);
      formData2.append('image_filename', fileUrl.split('/').pop());
      formData2.append('user_id', formData.user_id);
      formData2.append('audience', formData.audience);
      formData2.append('enabled', true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/banners/create`,
        formData2
      );

      if (response) {
        // console.log('responded', response.statusText);

        toast.success(`Banner uploaded successfully`, {
          theme: 'colored',
        });
        navigate('/banners/list');
      }
    } catch (err) {
      console.log('Error', err.message);
      toast.error(`Banner upload error`, {
        theme: 'colored',
        message: err.message,
      });
    }
  };

  const handleFieldChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      const image_file = files[0];
      setFormData({
        ...formData,
        image_file,
        image_filename: image_file.name,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <>
      <PageHeader
        title='Add a sliding banner'
        description='This adds a new banner image to the sliding images section on the home page'
        className='mb-3'
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header
          title='Details'
          light={false}
          noPreview={true}
        ></FalconComponentCard.Header>
        <Form onSubmit={handleSubmit}>
          <FalconComponentCard.Body>
            <Form.Label>Title</Form.Label>
            <Form.Control
              size='lg'
              type='text'
              placeholder=''
              name='title'
              onChange={handleFieldChange}
            />
            <br />

            <Row>
              <Col lg='6'>
                <FileUpload category={'banners'} setFileUrl={setFileUrl} />
              </Col>

              <Col>
                <Col lg='6'>
                  <Form.Group>
                    <Form.Label>Audience</Form.Label>
                    <Form.Select
                      size='md'
                      className='mb-3'
                      name='audience'
                      onChange={handleFieldChange}
                      defaultChecked={formData.audience}
                    >
                      {audiences.map((audience) => (
                        <option key={audience.value} value={audience.value}>
                          {audience.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lang='6'>
                  <Form.Group
                    controlId='formFileLg'
                    className='mb-3'
                    width='100%'
                  >
                    <Form.Label>Post date</Form.Label>
                    <br />
                    <DatePicker
                      selected={formData.post_date}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          post_date: value,
                        })
                      }
                      className='form-control'
                      placeholderText='DD-MM-YYYY H:mm'
                      timeIntervals={60}
                      dateFormat='dd-MM-yyyy H:mm'
                      showTimeSelect
                      fixedHeight
                      width='100%'
                      size='lg'
                      name='post_date'
                      autoComplete='off'
                      required
                    />
                  </Form.Group>
                </Col>

                <Col lang='6'>
                  <Form.Group
                    controlId='formFileLg'
                    className='mb-3'
                    width='100%'
                  >
                    <Form.Label>Expiry date</Form.Label>
                    <br />
                    <DatePicker
                      selected={formData.date_expiry}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          date_expiry: value,
                        })
                      }
                      className='form-control'
                      placeholderText='DD-MM-YYYY H:mm'
                      timeIntervals={60}
                      dateFormat='dd-MM-yyyy H:mm'
                      showTimeSelect
                      fixedHeight
                      width='100%'
                      size='lg'
                      name='date_expiry'
                    />
                  </Form.Group>
                </Col>
              </Col>
            </Row>

            <Row>
              <Col lg='6' className='mt-4 mb-3 ml-3'>
                <Form.Group>
                  <IconButton
                    className='mb-1'
                    variant='falcon-default'
                    size='lg'
                    icon='plus'
                    transform='shrink-3'
                    type='submit'
                  >
                    Submit
                  </IconButton>
                </Form.Group>
              </Col>
            </Row>
          </FalconComponentCard.Body>
        </Form>
      </FalconComponentCard>
    </>
  );
};

export default CreateHomeBannerForm;
